export interface Volume {
  number: string
  title: string
  subtitle: string
  lead?: string
  bulletsLabel?: string
  bullets: string[]
  result: string
  featured?: boolean
}

export const volumes: Volume[] = [
  {
    number: 'Volume 01',
    title: 'Makeup Foundations, Hygiene & Professional Setup',
    subtitle:
      'The professional foundation behind every well-structured makeup class.',
    bullets: [
      'Skin preparation',
      'Professional hygiene',
      'Brushes and tools',
      'Product categories',
      'Workstation setup',
      'Skin analysis',
      'Skin types',
      'Undertones',
      'Face analysis',
      'Product selection',
      'Sanitation',
      'Contamination prevention',
      'Professional habits',
    ],
    result:
      'Your students stop treating makeup as a collection of isolated techniques and begin working from a professional foundation.',
  },
  {
    number: 'Volume 02',
    title: 'Complexion, Color Correction & Base Techniques',
    subtitle:
      'Teach students how to create controlled, balanced and professional complexion work.',
    bullets: [
      'Foundation matching',
      'Undertone analysis',
      'Primers',
      'Concealer',
      'Color correction',
      'Coverage levels',
      'Layering',
      'Contour',
      'Bronzer',
      'Blush',
      'Highlight',
      'Powder',
      'Texture management',
      'Mature-skin considerations',
      'Complexion troubleshooting',
    ],
    result:
      'Students learn how to select, apply and adapt complexion products instead of simply copying the same base technique onto every face.',
  },
  {
    number: 'Volume 03',
    title: 'Eyes, Brows, Lashes & Precision Techniques',
    subtitle:
      'Build stronger control over the most detailed areas of makeup application.',
    bullets: [
      'Eye shapes',
      'Brow mapping',
      'Pressure control',
      'Stroke direction',
      'Eyeshadow placement',
      'Blending',
      'Transitions',
      'Eyeliner',
      'Mascara',
      'False lashes',
      'Smoky eyes',
      'Hooded eyes',
      'Deep-set eyes',
      'Symmetry',
      'Precision',
      'Common mistakes and corrections',
    ],
    result:
      'Students develop better control over placement, blending, symmetry and technical precision.',
  },
  {
    number: 'Volume 04',
    title: 'Lips, Face Balance & Complete Makeup Looks',
    subtitle:
      'A completely visual system for turning individual techniques into cohesive finished looks.',
    bullets: [
      'Lip analysis',
      'Lip mapping',
      "Cupid's bow",
      'Controlled overlining',
      'Lip symmetry',
      'Lip dimension',
      'Face balance',
      'Color harmony',
      'Natural Makeup',
      'Professional Day Makeup',
      'Soft Glam',
      'Evening Makeup',
      'Bold Lip Looks',
      'Full Glam',
      'Complete-look construction',
      'Troubleshooting',
      'Practical assessments',
    ],
    result:
      'Students stop thinking about eyes, cheeks and lips as separate pieces and begin creating complete, balanced makeup looks.',
  },
  {
    number: 'Volume 05',
    title: 'Bridal, Mature Skin, Diverse Clients & Advanced Applications',
    subtitle:
      'Teach students how to adapt professional makeup to real people and real situations.',
    bullets: [
      'Bridal workflow',
      'Long-wear makeup',
      'Mature skin',
      'Textured skin',
      'Dry skin',
      'Oily skin',
      'Different skin tones',
      'Different facial features',
      'Corrective techniques',
      'Event makeup',
      'Photography-ready makeup',
      'Lighting considerations',
      'Client adaptation',
      'Advanced professional decisions',
    ],
    result:
      'Students learn that professional makeup is not about repeating one technique on every client. It is about adapting the technique to the person in front of them.',
  },
  {
    number: 'Volume 06',
    title: '150 Ready-to-Teach Makeup Classes',
    subtitle: 'The practical heart of Makeup Instructor Ready.',
    lead: 'Choose the topic. Open the class. Follow the structure. Teach.',
    bulletsLabel: 'Each class can include:',
    bullets: [
      'Level',
      'Focus',
      'Total Time',
      'Demonstration',
      'Student Practice',
      'Technique Progression',
      'Common Mistakes',
      'Instructor Corrections',
      'Assessment',
      'Key Teaching Cue',
    ],
    result: 'You no longer have to create every makeup class from scratch.',
    featured: true,
  },
]

export interface LessonGroup {
  title: string
  detail: string
}

export const lessonGroups: LessonGroup[] = [
  {
    title: 'Foundations',
    detail:
      'Hygiene. Tools. Professional setup. Skin analysis. Skin preparation. Color theory. Face analysis. Product control.',
  },
  {
    title: 'Complexion',
    detail:
      'Foundation. Shade matching. Undertones. Concealer. Color correction. Contour. Bronzer. Blush. Highlight. Powder. Texture.',
  },
  {
    title: 'Eyes & Brows',
    detail:
      'Brow mapping. Eyeshadow. Blending. Transitions. Eye shapes. Eyeliner. Lashes. Symmetry. Precision.',
  },
  {
    title: 'Lips & Face Balance',
    detail:
      'Lip preparation. Lip mapping. Liner. Controlled overlining. Color placement. Lip symmetry. Face balance. Color harmony.',
  },
  {
    title: 'Complete Looks',
    detail:
      'Natural Makeup. Day Makeup. Professional Makeup. Soft Glam. Evening Makeup. Bold Lip. Full Glam.',
  },
  {
    title: 'Advanced Applications',
    detail:
      'Bridal. Mature skin. Textured skin. Different skin tones. Long-wear makeup. Photography. Event makeup. Troubleshooting. Client adaptation.',
  },
]
